
import React, { useState, useEffect } from 'react';
import { vipService, DEFAULT_VIP_CONFIGS } from '../services/vipService';
import { VIPConfig, VIPTierLevel } from '../types';
import { 
  Award, 
  Save, 
  Check, 
  Info, 
  Zap, 
  ShieldCheck, 
  TrendingUp, 
  Settings2, 
  DollarSign, 
  Percent,
  Sparkles,
  Users
} from 'lucide-react';

interface Props {
  storeId: string;
}

const AdminVIP: React.FC<Props> = ({ storeId }) => {
  const [configs, setConfigs] = useState<VIPConfig[]>([]);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setConfigs(vipService.getStoreVIPConfigs(storeId));
  }, [storeId]);

  const handleUpdate = (level: VIPTierLevel, field: keyof VIPConfig, value: any) => {
    setConfigs(prev => prev.map(c => c.level === level ? { ...c, [field]: value } : c));
  };

  const handleSave = () => {
    vipService.updateVIPConfigs(storeId, configs);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">VIP Club Benchmarks</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Customer Retention & Loyalty Hub</p>
        </div>
        <button 
          onClick={handleSave}
          className="bg-blue-600 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all flex items-center gap-2"
        >
          {isSaved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          {isSaved ? 'Rules Sync\'d' : 'Deploy Benchmarks'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-[40px] border border-gray-100 shadow-xl overflow-hidden">
               <div className="p-8 border-b border-gray-50 flex items-center gap-3">
                  <div className="p-3 bg-blue-50 text-blue-600 rounded-xl"><Award className="h-6 w-6" /></div>
                  <h3 className="text-xl font-black text-gray-900">Tier Configuration</h3>
               </div>
               <div className="p-8 space-y-10">
                  {configs.map((tier) => (
                    <div key={tier.level} className="p-8 rounded-[2.5rem] border-2 border-gray-50 bg-gray-50/30 space-y-6">
                       <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ backgroundColor: tier.color }}>
                             <Award className="h-6 w-6" />
                          </div>
                          <input 
                            value={tier.label}
                            onChange={e => handleUpdate(tier.level, 'label', e.target.value)}
                            className="text-xl font-black text-gray-900 bg-transparent border-none p-0 focus:ring-0 w-full"
                          />
                       </div>

                       <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div className="space-y-2">
                             <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                <DollarSign className="h-3 w-3" /> Spend Threshold ($)
                             </label>
                             <input 
                               type="number"
                               value={tier.spendThreshold}
                               onChange={e => handleUpdate(tier.level, 'spendThreshold', Number(e.target.value))}
                               className="w-full px-6 py-4 bg-white border-none rounded-2xl font-bold shadow-sm focus:ring-2 focus:ring-blue-500"
                             />
                             <p className="text-[9px] text-gray-400 font-medium">Customer hits this level after spending ${tier.spendThreshold}</p>
                          </div>
                          <div className="space-y-2">
                             <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                <Percent className="h-3 w-3" /> Permanent Discount (%)
                             </label>
                             <input 
                               type="number"
                               value={tier.discountPercentage}
                               onChange={e => handleUpdate(tier.level, 'discountPercentage', Number(e.target.value))}
                               className="w-full px-6 py-4 bg-white border-none rounded-2xl font-bold shadow-sm focus:ring-2 focus:ring-blue-500"
                             />
                             <p className="text-[9px] text-gray-400 font-medium">Applied automatically to every order</p>
                          </div>
                       </div>
                    </div>
                  ))}
               </div>
            </div>
         </div>

         <div className="space-y-8">
            <div className="bg-gray-900 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:scale-110 transition-transform"><Sparkles className="h-24 w-24" /></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">Psychology of VIP</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                     Bhai, data suggests customers spend 3x more when they are within 10% of the next VIP tier. These benchmarks are your most powerful retention tool.
                  </p>
                  <div className="p-6 bg-white/5 rounded-3xl border border-white/10 space-y-4">
                     <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-blue-400" />
                        <p className="text-xs font-bold uppercase tracking-widest">Boosts Lifetime Value (LTV)</p>
                     </div>
                     <div className="flex items-center gap-3">
                        <TrendingUp className="h-5 w-5 text-emerald-400" />
                        <p className="text-xs font-bold uppercase tracking-widest">Reduces Acquisition Cost</p>
                     </div>
                  </div>
               </div>
            </div>

            <div className="bg-blue-50 p-8 rounded-[2.5rem] border border-blue-100 flex items-start gap-4">
               <ShieldCheck className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
               <p className="text-[10px] text-blue-700 font-medium leading-relaxed uppercase tracking-wider">
                  Lynqar Rule: VIP Discounts are calculated before Tax and after standard Coupons.
               </p>
            </div>
         </div>
      </div>
    </div>
  );
};

export default AdminVIP;
